import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UrlApi } from "../../../../services/apirest";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function nuevaIdFila() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `fila-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @param {{
 *   filas: import("./correosPorAreaUtils").CorreoPorAreaFila[],
 *   onFilasChange: (next: import("./correosPorAreaUtils").CorreoPorAreaFila[]) => void,
 *   accentColor?: string,
 * }} props
 */
export default function CorreosPorAreaSection({
    filas,
    onFilasChange,
    accentColor = "#1F618D",
}) {
    const addBlockRef = useRef(null);
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(true);
    const [areasError, setAreasError] = useState(null);

    const [draftIdArea, setDraftIdArea] = useState("");
    const [draftCorreo, setDraftCorreo] = useState("");
    const [draftError, setDraftError] = useState(null);
    const [editingIdFila, setEditingIdFila] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoadingAreas(true);
        setAreasError(null);
        fetch(`${UrlApi}/proveedores/areas`)
            .then((r) => r.json())
            .then((data) => {
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                const activas = list.filter((a) => a?.activo !== 0);
                activas.sort((a, b) => Number(a.id) - Number(b.id));
                setAreas(activas);
            })
            .catch(() => {
                if (!cancelled) {
                    setAreas([]);
                    setAreasError("No se pudieron cargar las áreas. Intentá de nuevo más tarde.");
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingAreas(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const areasPorId = useMemo(() => {
        const m = new Map();
        areas.forEach((a) => m.set(Number(a.id), a));
        return m;
    }, [areas]);

    const nombreArea = useCallback(
        (id) => {
            const n = areasPorId.get(Number(id))?.nombre;
            return (n ?? `Área ${id}`).trim();
        },
        [areasPorId]
    );

    const resetDraft = useCallback(() => {
        setDraftIdArea("");
        setDraftCorreo("");
        setDraftError(null);
        setEditingIdFila(null);
    }, []);

    const handleAgregarFila = () => {
        const idNum = draftIdArea === "" ? NaN : Number(draftIdArea);
        const correoTrim = draftCorreo.trim();
        if (!Number.isFinite(idNum)) {
            setDraftError("Seleccioná un área");
            return;
        }
        if (!correoTrim) {
            setDraftError("Ingresá un correo");
            return;
        }
        if (!emailRegex.test(correoTrim)) {
            setDraftError("Formato de correo inválido");
            return;
        }

        const duplicado = filas.some(
            (f) =>
                f.id_area === idNum &&
                f.correo.toLowerCase() === correoTrim.toLowerCase() &&
                f.idFila !== editingIdFila
        );
        if (duplicado) {
            setDraftError("Ya existe esa combinación de área y correo");
            return;
        }

        if (editingIdFila) {
            onFilasChange(
                filas.map((f) =>
                    f.idFila === editingIdFila ? { ...f, id_area: idNum, correo: correoTrim } : f
                )
            );
        } else {
            onFilasChange([...filas, { idFila: nuevaIdFila(), id_area: idNum, correo: correoTrim }]);
        }
        resetDraft();
    };

    const handleEditar = (fila) => {
        setEditingIdFila(fila.idFila);
        setDraftIdArea(String(fila.id_area));
        setDraftCorreo(fila.correo);
        setDraftError(null);
        addBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    const handleEliminar = (idFila) => {
        onFilasChange(filas.filter((f) => f.idFila !== idFila));
        if (editingIdFila === idFila) resetDraft();
    };

    return (
        <Card
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                borderLeft: `5px solid ${accentColor}`,
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minWidth: 0,
                minHeight: 0,
                maxHeight: { xs: 420, sm: 450, md: 450 },
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ mb: 1, flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <EmailOutlinedIcon sx={{ mr: 1, color: accentColor, fontSize: 22 }} />
                    <Typography variant="button" sx={{ fontWeight: 700, color: accentColor }}>
                        Correos por área
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ pl: { sm: 4 } }}>
                    Agregá los correos que recibirán documentación o notificaciones según el área
                    correspondiente.
                </Typography>
            </Box>

            {areasError ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {areasError}
                </Alert>
            ) : null}

            {loadingAreas ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            overflowX: "auto",
                            mb: 1.5,
                            maxWidth: "100%",
                        }}
                    >
                    <TableContainer sx={{ maxWidth: "100%" }}>
                        <Table size="small" sx={{ minWidth: 400 }} stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Área</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Correo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
                                        Acciones
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                No hay correos cargados. Usá el formulario de abajo para
                                                agregar el primero.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filas.map((fila) => (
                                        <TableRow key={fila.idFila}>
                                            <TableCell>{nombreArea(fila.id_area)}</TableCell>
                                            <TableCell>{fila.correo}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    aria-label="Editar fila"
                                                    onClick={() => handleEditar(fila)}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    aria-label="Eliminar fila"
                                                    onClick={() => handleEliminar(fila.idFila)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>

                    <Box
                        ref={addBlockRef}
                        sx={{
                            flexShrink: 0,
                            pt: 1,
                            borderTop: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                        >
                            {editingIdFila ? "Editando fila seleccionada" : "Nueva fila"}
                        </Typography>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small" required>
                                    <InputLabel id="correo-area-draft-label">Área</InputLabel>
                                    <Select
                                        labelId="correo-area-draft-label"
                                        label="Área"
                                        value={draftIdArea}
                                        onChange={(e) => {
                                            setDraftIdArea(String(e.target.value));
                                            setDraftError(null);
                                        }}
                                        disabled={!areas.length}
                                    >
                                        <MenuItem value="">
                                            <em>Seleccionar</em>
                                        </MenuItem>
                                        {areas.map((a) => (
                                            <MenuItem key={a.id} value={String(a.id)}>
                                                {String(a.nombre ?? "").trim()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Correo"
                                    type="email"
                                    autoComplete="off"
                                    value={draftCorreo}
                                    onChange={(e) => {
                                        setDraftCorreo(e.target.value);
                                        setDraftError(null);
                                    }}
                                    error={!!draftError && draftCorreo.trim() !== ""}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={3}
                                sx={{ display: "flex", alignItems: "flex-start", pt: { md: 0.5 } }}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAgregarFila}
                                    disabled={!areas.length}
                                >
                                    {editingIdFila ? "Guardar" : "Agregar"}
                                </Button>
                                {editingIdFila ? (
                                    <Button size="small" onClick={resetDraft} sx={{ ml: 1 }}>
                                        Cancelar
                                    </Button>
                                ) : null}
                            </Grid>
                        </Grid>
                        {draftError ? (
                            <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                                {draftError}
                            </FormHelperText>
                        ) : null}
                    </Box>
                </>
            )}
        </Card>
    );
}
